import React, { useMemo } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import InvalidFeedback from '../../../../../components/forms/invalid-feedback.component';
import Icon from '../../../../../components/forms/icon.component';
import Switch from '../../../../../components/forms/switch.component';
import Input from '../../../../../components/forms/input.component';
import Title from '../../../../../components/forms/titles.component';
import useInputValidation from '../../../../../hooks/use-input-validation.hook';

export type EventType = 'failed' | 'followUp' | 'changeChannel';

export interface EventConfig {
  type: EventType;
  icon: string;
  title: string;
  description: string;
  fields: EventField[];
}

export interface EventField {
  name: string;
  label: string;
  type: 'select' | 'number' | 'checkbox' | 'template' | 'text';
  placeholder?: string;
  options?: { value: string; label: string }[];
  required?: boolean;
  validation?: any;
  colSize?: string;
  dependency?: string;
  conditionalRequired?: string;
}

interface EventsSectionProps {
  channel: 'wa' | 'sms' | 'rcs' | 'mail';
  events: EventConfig[];
  fallbackChannelOptions?: { value: string; label: string }[];
  templateOptions?: { value: string; label: string }[];
  onValidationChange?: (isValid: boolean) => void;
}

const timeUnitOptions = [
  { value: 'min', label: 'Minutos' },
  { value: 'hour', label: 'Horas' }
];

export const getChannelEventsConfig = (
  channel: 'wa' | 'sms' | 'rcs' | 'mail',
  fallbackChannelOptions: { value: string; label: string }[] = [],
  templateOptions: { value: string; label: string }[] = []
): EventConfig[] => {
  let failedChannelOptions = fallbackChannelOptions;

  if (channel === 'sms') {
    failedChannelOptions = fallbackChannelOptions.filter((option) => option.value === 'email');
  } else {
    failedChannelOptions = fallbackChannelOptions;
  }

  const baseEvents: Record<string, EventConfig> = {
    failed: {
      type: 'failed',
      icon: 'ki-cross-circle',
      title: 'Fallido',
      description: 'Enviar mensaje a canal de respaldo si falla.',
      fields: [
        {
          name: `events.${channel}.failedChannel`,
          label: 'Canal de respaldo',
          type: 'select',
          placeholder: 'Selecciona canal de respaldo',
          options: failedChannelOptions,
          required: true,
          colSize: 'col-md-6'
        }
      ]
    },
    followUp: {
      type: 'followUp',
      icon: 'ki-message-text-2',
      title: 'Mensaje de seguimiento',
      description: 'Enviar mensaje de seguimiento después de un tiempo.',
      fields: [
        {
          name: `events.${channel}.followUpTime`,
          label: 'Tiempo de espera',
          type: 'number',
          placeholder: 'Tiempo',
          required: true,
          colSize: 'col-md-6',
          validation: {
            min: { value: 0.01, message: 'Debe ser mayor que 0' }
          }
        },
        {
          name: `events.${channel}.followUpTimeUnit`,
          label: 'Unidad',
          type: 'select',
          placeholder: 'Minutos',
          options: timeUnitOptions,
          required: true,
          colSize: 'col-md-6'
        },

        {
          name: `events.${channel}.followUpOnSent`,
          label: 'Enviado',
          type: 'checkbox',
          colSize: 'col-md-3'
        },
        {
          name: `events.${channel}.followUpOnDelivered`,
          label: 'Entregado/Leído',
          type: 'checkbox',
          colSize: 'col-md-3'
        },

        {
          name: `events.${channel}.followUpTemplateSent`,
          label: 'Plantilla para Enviado',
          type: 'template',
          placeholder: 'Selecciona una plantilla',
          options: templateOptions,
          required: false,
          conditionalRequired: `events.${channel}.followUpOnSent`,
          colSize: 'col-md-6',
          dependency: `events.${channel}.followUpOnSent`
        },

        {
          name: `events.${channel}.followUpTemplateDelivered`,
          label: 'Plantilla para Entregado/Leído',
          type: 'template',
          placeholder: 'Selecciona una plantilla',
          options: templateOptions,
          required: false,
          conditionalRequired: `events.${channel}.followUpOnDelivered`,
          colSize: 'col-md-6',
          dependency: `events.${channel}.followUpOnDelivered`
        }
      ]
    },
    changeChannel: {
      type: 'changeChannel',
      icon: 'ki-arrow-up-down',
      title: 'Cambio de canal',
      description: 'Cambiar a otro canal después de un tiempo.',
      fields: [
        {
          name: `events.${channel}.changeChannelTime`,
          label: 'Tiempo de espera',
          type: 'number',
          placeholder: 'Minutos',
          required: true,
          colSize: 'col-md-6',
          validation: {
            min: { value: 2, message: 'Debe ser mayor que 1' },
            validate: (value: any) => Number.isInteger(+value) || 'Sólo se permiten números enteros'
          }
        },
        {
          name: `events.${channel}.changeChannelTimeUnit`,
          label: 'Unidad',
          type: 'select',
          placeholder: 'Minutos',
          options: timeUnitOptions,
          required: true,
          colSize: 'col-md-6'
        },
        {
          name: `events.${channel}.changeChannelOnSent`,
          label: 'Enviado',
          type: 'checkbox',
          colSize: 'col-md-3'
        },
        {
          name: `events.${channel}.changeChannelOnDelivered`,
          label: 'Entregado/Leído',
          type: 'checkbox',
          colSize: 'col-md-3'
        }
      ]
    }
  };

  switch (channel) {
    case 'wa':
      return [baseEvents.failed, baseEvents.followUp, baseEvents.changeChannel];

    case 'sms':
      return [baseEvents.failed];

    case 'rcs':
      return [baseEvents.failed, baseEvents.followUp, baseEvents.changeChannel];

    case 'mail':
      return [
        baseEvents.failed,
        {
          ...baseEvents.followUp,
          description: 'Enviar email de seguimiento después de un tiempo.'
        },
        baseEvents.changeChannel
      ];

    default:
      return [];
  }
};

function EventsSection({
  channel,
  events,
  fallbackChannelOptions = [],
  templateOptions = [],
  onValidationChange
}: EventsSectionProps) {
  const { t } = useTranslation();
  const { numericRegex } = useInputValidation();
  const { control, register, setValue, watch, clearErrors } = useFormContext();

  const formValues = watch();
  const currentEvents = formValues.events?.[channel] || {};
  const validateEvents = useMemo(() => {
    try {
      return events.every((event) => {
        const isEventActive = currentEvents[event.type];
        if (!isEventActive) return true;
        if (event.type === 'followUp' || event.type === 'changeChannel') {
          const hasOnSent = currentEvents[`${event.type}OnSent`];
          const hasOnDelivered = currentEvents[`${event.type}OnDelivered`];
          if (!hasOnSent && !hasOnDelivered) {
            return false;
          }
        }

        return event.fields.every((field) => {
          const fieldPath = field.name.split('.').pop();
          if (!fieldPath) return false;

          const fieldValue = currentEvents[fieldPath];

          if (field.conditionalRequired) {
            const dependencyPath = field.conditionalRequired.split('.').pop();
            const isDependencyActive = dependencyPath ? currentEvents[dependencyPath] : false;

            if (isDependencyActive) {
              return fieldValue !== undefined && fieldValue !== null && fieldValue !== '';
            }

            return true;
          }

          if (field.required) {
            return fieldValue !== undefined && fieldValue !== null && fieldValue !== '';
          }

          return true;
        });
      });
    } catch (error) {
      console.error('Error validating events:', error);
      return false;
    }
  }, [currentEvents, events]);

  React.useEffect(() => {
    if (onValidationChange) {
      onValidationChange(validateEvents);
    }
  }, [validateEvents, onValidationChange]);

  const handleEventToggle = React.useCallback(
    (eventType: EventType, isEnabled: boolean) => {
      setValue(`events.${channel}.${eventType}`, isEnabled);

      if (!isEnabled) {
        const eventConfig = events.find((e) => e.type === eventType);
        if (eventConfig) {
          eventConfig.fields.forEach((field) => {
            const fieldPath = field.name.split('.').pop();
            if (fieldPath) {
              const defaultValue = field.type === 'checkbox' ? false : '';
              setValue(`events.${channel}.${fieldPath}`, defaultValue);
              clearErrors(`events.${channel}.${fieldPath}`);
            }
          });
        }
      }
    },
    [channel, events, setValue, clearErrors]
  );

  const renderField = React.useCallback(
    (field: EventField, eventType: EventType) => {
      const isEventActive = currentEvents[eventType];
      const fieldPath = field.name.split('.').pop();
      const fieldName = `events.${channel}.${fieldPath}`;

      if (!isEventActive) return null;

      if (field.dependency) {
        const dependencyPath = field.dependency.split('.').pop();
        const isDependencyActive = dependencyPath ? currentEvents[dependencyPath] : false;
        if (!isDependencyActive) return null;
      }

      const isConditionallyRequired = field.conditionalRequired
        ? (() => {
            const dependencyPath = field.conditionalRequired.split('.').pop();
            return dependencyPath ? currentEvents[dependencyPath] : false;
          })()
        : false;

      const isFieldRequired = field.required || isConditionallyRequired;

      switch (field.type) {
        case 'select':
        case 'template':
          return (
            <div
              key={field.name}
              className={`${field.colSize} mb-3 animate-fadeInUp animate-delay-1 z-index-3`}
            >
              <label className={`form-label fw-bolder fs-6 ${isFieldRequired ? 'required' : ''}`}>
                {field.label}
              </label>
              <Controller
                control={control}
                name={fieldName}
                rules={{ required: isEventActive ? isFieldRequired : false }}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <>
                    <Select
                      options={field.options || []}
                      noOptionsMessage={({ inputValue }) =>
                        inputValue
                          ? t('general.no-options', { inputValue })
                          : t('general.no-options')
                      }
                      className={error ? 'form-select-custom is-invalid' : 'form-select-custom'}
                      classNamePrefix="form-select-custom"
                      placeholder={field.placeholder}
                      onChange={(selectedOption) => {
                        onChange(selectedOption ? selectedOption.value : null);
                      }}
                      value={field.options?.find((option) => option.value === value) || null}
                      isDisabled={!field.options || field.options.length === 0}
                    />
                    <InvalidFeedback error={error} />
                    {(!field.options || field.options.length === 0) && (
                      <div className="text-muted mt-2">
                        {field.type === 'template'
                          ? 'No hay plantillas disponibles.'
                          : channel === 'sms'
                            ? 'No hay email disponible como canal de respaldo.'
                            : 'No hay canales disponibles. Activa más canales en el paso 4.'}
                      </div>
                    )}
                  </>
                )}
              />
            </div>
          );

        case 'number':
          return (
            <div
              key={field.name}
              className={`${field.colSize} mb-3 animate-fadeInUp animate-delay-1`}
            >
              <label className={`form-label fw-bolder fs-6 ${isFieldRequired ? 'required' : ''}`}>
                {field.label}
              </label>
              <Input
                type="number"
                placeholder={field.placeholder}
                min={1}
                step={1}
                inputMode="numeric"
                onKeyDown={(e: any) => {
                  if (['e', 'E', '+', '-', '.'].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                {...register(fieldName, {
                  required: isEventActive && isFieldRequired ? 'Este campo es requerido' : false,
                  pattern: field.validation?.pattern || {
                    value: numericRegex,
                    message: t('forms.invalid-FeedBack.valid-price')
                  },
                  ...field.validation
                })}
              />
            </div>
          );

        case 'text':
          return (
            <div
              key={field.name}
              className={`${field.colSize} mb-3 animate-fadeInUp animate-delay-1`}
            >
              <label className={`form-label fw-bolder fs-6 ${isFieldRequired ? 'required' : ''}`}>
                {field.label}
              </label>
              <Input
                type="text"
                placeholder={field.placeholder}
                {...register(fieldName, {
                  required: isEventActive ? isFieldRequired : false,
                  setValueAs: (value: string) => value?.trim() || ''
                })}
              />
            </div>
          );

        case 'checkbox':
          return (
            <div
              key={field.name}
              className={`${field.colSize} mb-3 animate-fadeInUp animate-delay-4`}
            >
              <div className="form-check form-check-custom form-check-solid p-3 border border-dashed border-gray-300 rounded bg-light-info">
                <input className="form-check-input" type="checkbox" {...register(fieldName)} />
                <label className="form-check-label fw-bold text-dark">
                  <Icon icon="ki-notification-bing" size="fs-6" type="info" className="me-2" />
                  {field.label}
                </label>
                <div className="text-muted fs-7 mt-1">
                  {field.label === 'Enviado'
                    ? 'Activar cuando el mensaje sea enviado'
                    : 'Activar cuando el mensaje sea entregado o leído'}
                </div>
              </div>
            </div>
          );

        default:
          return null;
      }
    },
    [currentEvents, channel, control, register, t, numericRegex]
  );

  const renderEventActions = (eventType: EventType) => {
    const hasOnSent = currentEvents[`${eventType}OnSent`];
    const hasOnDelivered = currentEvents[`${eventType}OnDelivered`];
    const isValidSelection = hasOnSent || hasOnDelivered;

    return (
      <>
        {/* Sección de Triggers */}
        <div className="col-12">
          <label className="form-label fw-bolder fs-6 required">
            {eventType === 'followUp' ? '¿Cuándo enviar?' : '¿Cuándo cambiar?'}
          </label>
          {!isValidSelection && (
            <div className="text-warning fw-bold fs-7 mb-2">
              <Icon icon="ki-warning" size="fs-8" className="me-1" />
              Selecciona al menos una opción
            </div>
          )}
          <div className="px-3 py-0">
            <div className="d-flex align-items-center justify-content-start">
              {/* Checkbox Enviado */}
              <div className="form-check form-check-custom form-check-solid mb-2 col-6">
                <input
                  className="form-check-input"
                  type="checkbox"
                  {...register(`events.${channel}.${eventType}OnSent`)}
                />
                <Icon icon="ki-send" size="fs-2" type="primary" className=" ms-2" />
                <label className="form-check-label fw-semibold">Enviado</label>
              </div>

              {/* Checkbox Entregado/Leído */}
              <div className="form-check form-check-custom form-check-solid mb-2 d-flex align-items-center col-6 ps-9">
                <input
                  className="form-check-input"
                  type="checkbox"
                  {...register(`events.${channel}.${eventType}OnDelivered`)}
                />
                <Icon icon="ki-double-check" size="fs-2x" type="primary" className=" ms-2" />
                <label className="form-check-label fw-semibold">Entregado/Leído</label>
              </div>
            </div>
          </div>
        </div>

        {/* Sección de Acciones Condicionales */}
        {renderConditionalActions(eventType, hasOnSent, hasOnDelivered)}
      </>
    );
  };

  const renderConditionalActions = (
    eventType: EventType,
    hasOnSent: boolean,
    hasOnDelivered: boolean
  ) => {
    if (eventType === 'followUp') {
      return renderTemplateActions(hasOnSent, hasOnDelivered);
    } else if (eventType === 'changeChannel') {
      return renderChannelActions(hasOnSent, hasOnDelivered);
    }
    return null;
  };

  const renderTemplateActions = (hasOnSent: boolean, hasOnDelivered: boolean) => {
    return (
      <div className="col-12 mb-3">
        <div className={`px-3 py-0 d-flex gap-3 ${hasOnSent ? '' : 'justify-content-end'} `}>
          {/* Plantilla para Enviado */}
          {hasOnSent && (
            <div className="mb-3 col-6 animate-fadeInUp pe-3">
              <div className="d-flex align-items-center mb-2">
                <span className="fw-semibold fs-6">
                  Plantilla para Enviado <label className="form-label required"></label>
                </span>
              </div>
              <Controller
                control={control}
                name={`events.${channel}.followUpTemplateSent`}
                rules={{ required: hasOnSent }}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <>
                    <Select
                      options={templateOptions || []}
                      noOptionsMessage={({ inputValue }) =>
                        inputValue
                          ? t('general.no-options', { inputValue })
                          : t('general.no-options')
                      }
                      className={
                        error ? 'form-select-custom is-invalid' : 'form-select-custom mt-2'
                      }
                      classNamePrefix="form-select-custom"
                      placeholder="Selecciona una plantilla"
                      onChange={(selectedOption) => {
                        onChange(selectedOption ? selectedOption.value : null);
                      }}
                      value={templateOptions?.find((option) => option.value === value) || null}
                      isDisabled={!templateOptions || templateOptions.length === 0}
                    />
                    <InvalidFeedback error={error} />
                    {(!templateOptions || templateOptions.length === 0) && (
                      <div className="text-muted mt-2">No hay plantillas disponibles.</div>
                    )}
                  </>
                )}
              />
            </div>
          )}

          {/* Plantilla para Entregado/Leído */}
          {hasOnDelivered && (
            <div className="mb-3 col-6 animate-fadeInUp ps-3">
              <div className="d-flex align-items-center mb-2">
                <span className="fw-semibold fs-6">
                  Plantilla para Entregado/Leído <label className="form-label required"></label>
                </span>
              </div>
              <Controller
                control={control}
                name={`events.${channel}.followUpTemplateDelivered`}
                rules={{ required: hasOnDelivered }}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <>
                    <Select
                      options={templateOptions || []}
                      noOptionsMessage={({ inputValue }) =>
                        inputValue
                          ? t('general.no-options', { inputValue })
                          : t('general.no-options')
                      }
                      className={error ? 'form-select-custom is-invalid' : 'form-select-custom'}
                      classNamePrefix="form-select-custom"
                      placeholder="Selecciona una plantilla"
                      onChange={(selectedOption) => {
                        onChange(selectedOption ? selectedOption.value : null);
                      }}
                      value={templateOptions?.find((option) => option.value === value) || null}
                      isDisabled={!templateOptions || templateOptions.length === 0}
                    />
                    <InvalidFeedback error={error} />
                    {(!templateOptions || templateOptions.length === 0) && (
                      <div className="text-muted mt-2">No hay plantillas disponibles.</div>
                    )}
                  </>
                )}
              />
            </div>
          )}
        </div>
      </div>
    );
  };
  const renderChannelActions = (hasOnSent: boolean, hasOnDelivered: boolean) => {
    // const sentChannelValue = currentEvents.changeChannelTargetSent;
    // const deliveredChannelValue = currentEvents.changeChannelTargetDelivered;
    return (
      <div className="col-12 mb-3">
        <div className={`px-3 py-0 d-flex gap-3 ${hasOnSent ? '' : 'justify-content-end'} `}>
          {/* Canal para Enviado */}
          {hasOnSent && (
            <div className="mb-3 col-6 animate-fadeInUp pe-3">
              <div className="d-flex align-items-center mb-2">
                <span className="fw-semibold fs-6">
                  Canal para Enviado <label className="form-label required"></label>
                </span>
              </div>
              <Controller
                control={control}
                name={`events.${channel}.changeChannelTargetSent`}
                rules={{ required: hasOnSent }}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <>
                    <Select
                      options={fallbackChannelOptions || []}
                      noOptionsMessage={({ inputValue }) =>
                        inputValue
                          ? t('general.no-options', { inputValue })
                          : t('general.no-options')
                      }
                      className={error ? 'form-select-custom is-invalid' : 'form-select-custom'}
                      classNamePrefix="form-select-custom"
                      placeholder="Selecciona canal"
                      onChange={(selectedOption) => {
                        onChange(selectedOption ? selectedOption.value : null);
                      }}
                      value={
                        fallbackChannelOptions?.find((option) => option.value === value) || null
                      }
                      isDisabled={!fallbackChannelOptions || fallbackChannelOptions.length === 0}
                    />
                    <InvalidFeedback error={error} />
                    {(!fallbackChannelOptions || fallbackChannelOptions.length === 0) && (
                      <div className="text-muted mt-2">
                        No hay canales disponibles. Activa más canales en el paso 4.
                      </div>
                    )}
                  </>
                )}
              />
            </div>
          )}

          {/* Canal para Entregado/Leído */}
          {hasOnDelivered && (
            <div className="mb-3 col-6 animate-fadeInUp ps-3">
              <div className="d-flex align-items-center mb-2">
                <span className="fw-semibold fs-6">
                  Canal para Entregado/Leído <label className="form-label required"></label>
                </span>
              </div>
              <Controller
                control={control}
                name={`events.${channel}.changeChannelTargetDelivered`}
                rules={{ required: hasOnDelivered }}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <>
                    <Select
                      options={fallbackChannelOptions || []}
                      noOptionsMessage={({ inputValue }) =>
                        inputValue
                          ? t('general.no-options', { inputValue })
                          : t('general.no-options')
                      }
                      className={error ? 'form-select-custom is-invalid' : 'form-select-custom'}
                      classNamePrefix="form-select-custom"
                      placeholder="Selecciona canal"
                      onChange={(selectedOption) => {
                        onChange(selectedOption ? selectedOption.value : null);
                      }}
                      value={
                        fallbackChannelOptions?.find((option) => option.value === value) || null
                      }
                      isDisabled={!fallbackChannelOptions || fallbackChannelOptions.length === 0}
                    />
                    <InvalidFeedback error={error} />
                    {(!fallbackChannelOptions || fallbackChannelOptions.length === 0) && (
                      <div className="text-muted mt-2">
                        No hay canales disponibles. Activa más canales en el paso 4.
                      </div>
                    )}
                  </>
                )}
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  const channelNames = {
    wa: 'WhatsApp',
    sms: 'SMS',
    rcs: 'RCS',
    mail: 'Email'
  };

  return (
    <div className="mb-5">
      <div className="separator separator-dashed separator-content text-dark mt-15 mb-5">
        <img alt="Logo" src="/assets/media/logos/logo-c.svg" className="w-30px logo-minimize" />
      </div>

      <Title
        icon="ki-calendar"
        title={`Eventos ${channelNames[channel]}`}
        subtitle="Configura los eventos que deseas que se ejecuten automáticamente."
        type="info"
      />

      <div className="alert bg-light-warning border border-warning border-dashed d-flex flex-column flex-sm-row p-5 mb-10 animate-fadeInUp animate-delay-1">
        <p className="mb-0">
          Los eventos permiten enviar notificaciones por inactividad o cambiar a un canal
          alternativo previamente configurado, lo que incrementa las probabilidades de entrega del
          mensaje.
        </p>
      </div>

      {events.map((eventConfig, index) => {
        const isEventActive = currentEvents[eventConfig.type];

        return (
          <div key={eventConfig.type}>
            <div className="fv-row mb-1">
              <div className="d-flex flex-stack cursor-pointer mb-3 ms-2">
                <div className="d-flex align-items-center me-2">
                  <span className="symbol me-6 symbol-label">
                    <Icon icon={eventConfig.icon} size="fs-3x" />
                  </span>
                  <div className="d-flex flex-column">
                    <span className="fw-bolder fs-3">
                      {eventConfig.title} <span className="fs-7">({t('general.optional')})</span>
                    </span>
                    <span className="fs-6">{eventConfig.description}</span>
                  </div>
                </div>
                <div className="form-check form-check-custom form-check-solid">
                  <Switch
                    {...register(`events.${channel}.${eventConfig.type}`)}
                    onChange={(e: any) => {
                      handleEventToggle(eventConfig.type, e.target.checked);
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="fv-row mb-3">
              {isEventActive && (
                <div className="row mb-3">
                  {eventConfig.fields
                    .filter((field) => field.type !== 'checkbox' && field.type !== 'template')
                    .map((field) => renderField(field, eventConfig.type))}

                  {(eventConfig.type === 'followUp' || eventConfig.type === 'changeChannel') &&
                    renderEventActions(eventConfig.type)}
                </div>
              )}
            </div>

            {index < events.length - 1 && (
              <div className="separator separator-dashed text-dark mb-5"></div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default EventsSection;
